import { useCallback, useEffect, useState } from "react";
import { Banner, Button, EmptyState, Segmented, Select, Spinner, Tag, TextArea, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import { aiApi, type AiFeedbackDto } from "../../../api/aiApi";
import "./aiFeedback.css";

/**
 * «עוזר AI — משובים» (ADR-0035, like Turbo's AiFeedbackAdminTab): the 👍 / 👎 the organization's users
 * gave AI answers, with the question and the answer, a status and a note (ai.usage.view).
 */
const SURFACES: Record<string, string> = {
  assistant: "עוזר AI",
  automation_designer: "מעצב אוטומציות",
  data_entry: "הזנה חכמה",
  data_audit: "בדיקת נתונים",
};
const STATUS: Array<{ value: AiFeedbackDto["status"]; label: string }> = [
  { value: "new", label: "חדש" },
  { value: "reviewed", label: "נבדק" },
  { value: "resolved", label: "טופל" },
  { value: "dismissed", label: "לא רלוונטי" },
];

function FeedbackRow({ item, onChange }: { item: AiFeedbackDto; onChange: (next: AiFeedbackDto) => void }) {
  const toast = useCkToast();
  const [note, setNote] = useState(item.adminNote ?? "");
  const [busy, setBusy] = useState(false);
  const save = async (patch: { status?: AiFeedbackDto["status"]; adminNote?: string | null }) => {
    setBusy(true);
    try {
      const res = await aiApi.updateFeedback(item.id, patch);
      onChange({ ...item, status: res.status as AiFeedbackDto["status"], adminNote: res.adminNote });
    } catch (e) {
      toast.error((e as { message?: string })?.message || "השמירה נכשלה");
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="ai-fbl__item">
      <header className="ai-fbl__top">
        <span className="ai-fbl__rating" aria-label={item.rating === "up" ? "חיובי" : "שלילי"}>
          {item.rating === "up" ? "👍" : "👎"}
        </span>
        <Tag>{SURFACES[item.surface] ?? item.surface}</Tag>
        <small>
          {item.userName ?? "משתמש"} · {new Date(item.updatedAt).toLocaleString("he-IL", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })}
          {item.pagePath ? ` · ${item.pagePath}` : ""}
        </small>
        <Select
          aria-label="סטטוס"
          className="ai-fbl__status"
          value={item.status}
          options={STATUS}
          disabled={busy}
          onChange={(v) => void save({ status: v as AiFeedbackDto["status"] })}
        />
      </header>
      {item.comment ? <p className="ai-fbl__comment">«{item.comment}»</p> : null}
      {item.question ? (
        <div className="ai-fbl__qa">
          <b>שאלה</b>
          <p>{item.question}</p>
        </div>
      ) : null}
      {item.answer ? (
        <div className="ai-fbl__qa">
          <b>תשובה</b>
          <p>{item.answer}</p>
        </div>
      ) : null}
      <div className="ai-fbl__note">
        <TextArea rows={1} value={note} maxLength={2000} placeholder="הערה פנימית (לא נשלחת למשתמש)" onChange={(e) => setNote(e.target.value)} />
        <Button size="sm" disabled={busy || note === (item.adminNote ?? "")} onClick={() => void save({ adminNote: note.trim() || null })}>
          שמירה
        </Button>
      </div>
    </article>
  );
}

export function AiFeedbackPanel() {
  const [rating, setRating] = useState<"all" | "up" | "down">("down");
  const [items, setItems] = useState<AiFeedbackDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setItems(null);
    setError(null);
    try {
      setItems(await aiApi.listFeedback(rating === "all" ? {} : { rating }));
    } catch (e) {
      setError((e as { message?: string })?.message || "לא הצלחנו לטעון משובים");
    }
  }, [rating]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="ai-fbl">
      <Segmented
        ariaLabel="סינון משובים"
        value={rating}
        onChange={(v) => setRating(v as typeof rating)}
        items={[
          { value: "down", label: "👎 שליליים" },
          { value: "up", label: "👍 חיוביים" },
          { value: "all", label: "הכל" },
        ]}
      />
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {!items && !error ? <Spinner /> : null}
      {items && !items.length ? <EmptyState icon="smile" title="אין משובים להצגה" /> : null}
      {items?.map((it) => (
        <FeedbackRow key={it.id} item={it} onChange={(next) => setItems((list) => list?.map((x) => (x.id === next.id ? next : x)) ?? null)} />
      ))}
    </div>
  );
}
